import React, { useEffect, useRef, useState } from "react";
import { loadBaseWines, loadWineVintages, loadProducers, useWineContext, loadWines, loadInitialWines } from "../../../context/wineContext";
import BaseWineFilters from "../../global/BaseWines/BaseWineFilters";
import Button from "../../global/Buttons/Button";
import BaseWineSearchBar from "../../global/SearchBar/BaseWineSearchBar";
import BaseWineResults from "./BaseWineResults";
import { NumberParam, ObjectParam, QueryParamConfig, StringParam, useQueryParams, withDefault } from "use-query-params";
import { IFilter } from "../../global/Wines/WineFilters";
import _ from "lodash";
import { useRouter } from "next/router";
import { formatFilterParams, yearRegex } from "../../../utils/wine";
import { useToast } from "../../../context/toastContext";

interface IWineSearchResultsProps {
  initialWines: any;
  errorLoading?: string;
}
const FilterParamsType = withDefault(ObjectParam, {}) as QueryParamConfig<IFilter>;

const WineSearchResults = ({ initialWines, errorLoading  }: IWineSearchResultsProps) => {

  const [queryParams, setQueryParams] = useQueryParams({
    filters: FilterParamsType,
    displayType: StringParam,
    sort: StringParam,
    sortDirection: StringParam,
    term: StringParam,
    page: NumberParam,
    per_page: NumberParam,
  });

  const [ legacyQueries ] = useQueryParams({
    q: StringParam,
    query: StringParam,
    'wine_filter[vintage_range_min]': NumberParam,
    'wine_filter[vintage_range_max]': NumberParam,
    'wine_filter[score_range_min]': NumberParam,
    'wine_filter[score_range_max]': NumberParam,
    'wine_filter[price_range_min]': NumberParam,
    'wine_filter[price_range_max]': NumberParam,
    'wine_filter[review_date_year_range_min]': NumberParam,
    'wine_filter[review_date_year_range_max]': NumberParam,
    'wine_filter[color]': StringParam,
    'wine_filter[country]': StringParam,
    'wine_filter[region]': StringParam,
    'wine_filter[author]': StringParam,
  });

  const {
    filters: filterQuery,
    displayType: searchType,
    sort,
    sortDirection,
    term,
    page,
    per_page: perPage
  } = queryParams;

  // state
  const [filterOpen, setFilterOpen] = useState(false);
  const [year , setYear] = useState("")

  // context
  const {
    state: { loading, error },
    dispatch: winesDispatch,
  } = useWineContext();

  const router = useRouter();
  const firstLoad = useRef(true);
  const toast = useToast();

  useEffect(() => {
    if (errorLoading?.length > 0) {
      toast("error", errorLoading);
    }
    if (firstLoad.current && router.isReady) {
      const dispatchType =
        searchType === "reviews"
          ? "setWines"
          : searchType === "baseWine"
          ? "setBaseWines"
          : searchType === "wineVintage"
          ? "setWineVintages"
          : searchType === "producer"
          ? "setProducers"
          : "setWines"
      loadInitialWines(initialWines, winesDispatch, dispatchType);
      firstLoad.current = false;
    } else {
      if (Object.values(legacyQueries).some((q) => q)) handleLegacyQueries();
      if (router.isReady && !loading) handleSearch();
    }

  }, [router.isReady, term, filterQuery, sort, sortDirection, page, perPage, searchType])

  const handleLegacyQueries = () => {
    const legacyFilters = {} as IFilter;
    if (legacyQueries['q']) legacyFilters.searchable = legacyQueries['q'];
    if (legacyQueries['query']) legacyFilters.searchable = legacyQueries['query'];
    if (legacyQueries['wine_filter[vintage_range_min]']) legacyFilters.vintageYearMin = legacyQueries['wine_filter[vintage_range_min]'];
    if (legacyQueries['wine_filter[vintage_range_max]']) legacyFilters.vintageYearMax = legacyQueries['wine_filter[vintage_range_max]'];
    if (legacyQueries['wine_filter[score_range_min]']) legacyFilters.scoreBetweenMin = legacyQueries['wine_filter[score_range_min]'];
    if (legacyQueries['wine_filter[score_range_max]']) legacyFilters.scoreBetweenMax = legacyQueries['wine_filter[score_range_max]'];
    if (legacyQueries['wine_filter[price_range_min]']) legacyFilters.priceBetweenMin = legacyQueries['wine_filter[price_range_min]'];
    if (legacyQueries['wine_filter[price_range_max]']) legacyFilters.priceBetweenMax = legacyQueries['wine_filter[price_range_max]'];
    if (legacyQueries['wine_filter[review_date_year_range_min]']) legacyFilters.reviewDateMin = legacyQueries['wine_filter[review_date_year_range_min]'];
    if (legacyQueries['wine_filter[review_date_year_range_max]']) legacyFilters.reviewDateMax = legacyQueries['wine_filter[review_date_year_range_max]'];
    if (legacyQueries['wine_filter[color]']) legacyFilters.color = legacyQueries['wine_filter[color]'];
    if (legacyQueries['wine_filter[country]']) legacyFilters.country = legacyQueries['wine_filter[country]'];
    if (legacyQueries['wine_filter[author]']) legacyFilters.author = legacyQueries['wine_filter[author]'];

    setQueryParams({ ...queryParams, filters: { ...filterQuery, ...legacyFilters }});
  }

  const handleSearch = () => {
    setYear("")
    let formattedTerm = term || "";
    let words = formattedTerm.split(" ");
    let vintage: string;
    for (let word of words) {
      let potentialYear = Number(word);
      if (!isNaN(potentialYear) && potentialYear >= 1900 && potentialYear <= new Date().getFullYear()) {
        vintage = word;
        break;
      }
    }

    const searchTypeWithDefault = searchType || "reviews";
    const paginationWithDefaults = {
      page: page || 1,
      per_page: perPage || 25,
    };
    const sortWithDefaults = {
      order: sort || "",
      order_type: sortDirection || "desc",
    };

    if(vintage) {
      setYear(vintage)
      formattedTerm = formattedTerm.replace(vintage, '');
    }

    const params = {
      term: term ? formattedTerm : "*",
      vintage: vintage ? parseInt(vintage) : null,
      ...sortWithDefaults,
      ...paginationWithDefaults,
      ...formatFilterParams(filterQuery)
    };

    switch(searchTypeWithDefault) {
      case "baseWine":
        loadBaseWines(params, winesDispatch);
        break;
      case "wineVintage":
        loadWineVintages(params, winesDispatch);
        break;
      case "producer":
        loadProducers(params, winesDispatch);
        break;
      case "reviews":
        loadWines(params, winesDispatch);
      default:
        break;
    }
  }

  const handleTypeChange = (type) => {
    setQueryParams({
      ...queryParams,
      displayType: type,
      page: 1,
    });
  }

  const handlePageChange = ({ page, per_page }) => {
    setQueryParams({
      ...queryParams,
      page,
      per_page
    })
  }

  return (
    <div className="pb-8">
      <BaseWineSearchBar
        hideResults={true}
        className="pb-2 md:pb-6"
        value={term}
        key={term}
        onSearch={(e) => {
          setQueryParams({
            ...queryParams,
            term: e,
            page: 1,
          });
        }}
      />
      <div className="lg:flex lg:items-start lg:space-x-8 relative">
        <BaseWineFilters
          additionalClassName={`lg:block ${ filterOpen ? "block" : "hidden" }`}
          values={{}}
          sortValue={sort || ''}
          sortDirection={sortDirection || 'desc'}
          onApply={(newFilters) => {
            setQueryParams({
              ...queryParams,
              filters: newFilters,
            });
            setFilterOpen(false);
          }}
          onSortChange={(e) => {
            setQueryParams({
              ...queryParams,
              sort: e.value,
              page: null,
              per_page: null,
            });
          }}
          onSortDirectionChange={(e) => {
            setQueryParams({
              ...queryParams,
              sortDirection: e === "asc" ? "asc" : "desc",
              page: null,
              per_page: null,
            });
          }}
          filters={filterQuery}
          setFilters={(newFilters) => {
            window.scrollTo({top: 0, behavior: 'smooth'})
            setQueryParams({
              ...queryParams,
              page: 1,
              filters: newFilters,
            })
          }}
        />
        <Button
          type="primary"
          label="Show Advanced Options"
          labelColor="text-black"
          className="mb-2 lg:hidden bg-opacity-20"
          onClick={() => {
            setFilterOpen(true);
          }}
        />

        <div className="space-y-8 w-full">
          {
            error && error
          }
          {
            !error &&
            <div className="space-y-8">
              <BaseWineResults
                term={term || ""}
                type={searchType || "reviews"}
                year={year}
                onTypeChange={handleTypeChange}
                onPageChange={handlePageChange}
              />
            </div>
          }
        </div>
      </div>
    </div>
  )
}

export default WineSearchResults;
